import { useEffect, useRef, useState } from 'react';
import {
  callDispositionLabels,
  callDispositions,
  formatCallDuration,
  formatPhoneDisplay,
  formatTime,
  toBerlinDateKey,
  type CallDisposition,
} from '@brimo/shared';
import { Banner } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useTelephonyStatus } from '../../telephony/api';
import { CallBar } from '../../telephony/CallBar';
import { LiveTranscriptPanel } from '../../telephony/LiveTranscriptPanel';
import { elapsedSeconds, formatCallTimer, isLive, type CallSnapshot } from '../../telephony/callState';
import { useCall, useClock } from '../../telephony/useCall';
import {
  noteWithSummary,
  SUMMARY_LOADING_TEXT,
  SUMMARY_SUGGESTION_TEXT,
  useCallSummary,
} from '../../telephony/useCallSummary';
import type { CallSummary } from '../../telephony/api';
import { completeTask, logCall } from '../api';
import { addDays, todayKey } from '../format';
import type { LogCallBody, LogCallResponse } from '../types';
import { EditorCard, PrimaryAction } from './EditorCard';
import { ChipGroup, EditorField, FollowUpField, validTime } from './fields';
import { followUpOf, isNextStepRequired, isValidation, useNextStepGate, type EditorProps } from './shared';
import styles from './editor.module.css';

interface Errors {
  note?: string | undefined;
  followUp?: string | undefined;
}

const DISPOSITIONS = callDispositions.map((value) => ({ value, label: callDispositionLabels[value] }));

/** The task the server creates for a disposition (api-contacts.md „Manual call log"). */
function followUpTitle(disposition: CallDisposition, name: string): string | null {
  switch (disposition) {
    case 'reached':
      return `Follow-up: ${name}`;
    case 'callback':
    case 'no_interest':
      return `Wiedervorlage: ${name}`;
    case 'not_reached':
    case 'voicemail':
      return `Erneut anrufen: ${name}`;
    case 'documents_promised':
      return `Unterlagen nachfassen: ${name}`;
    case 'dnc':
      return null;
  }
}

/** The E1 chip for a suggested disposition of the AI summary (none for „Falsche Nummer"). */
function editorChipOf(value: CallSummary['suggestedDisposition']): CallDisposition | null {
  switch (value) {
    case 'appointment_set':
      return 'reached';
    case 'follow_up':
    case 'callback_requested':
      return 'callback';
    case 'not_reached':
      return 'not_reached';
    case 'voicemail':
      return 'voicemail';
    case 'no_interest':
      return 'no_interest';
    case 'block':
      return 'dnc';
    case 'documents_promised':
      return 'documents_promised';
    case 'wrong_number':
    case null:
      return null;
  }
}

/** The number after „von" in the record's „Versuche" row („2 von 5" → "5"). */
function maxAttemptsOf(attempts: string | null | undefined): string | null {
  return attempts ? (/von (\d+)/.exec(attempts)?.[1] ?? null) : null;
}

/** E1 „Status" while the softphone is used: „Klingelt · Versuch 1 von 3. Der Zähler läuft mit, …". */
function softphoneStatus(state: CallSnapshot, seconds: number | null, maxAttempts: string | null): string {
  const attempt = state.view?.attemptNumber ?? null;
  const counter = attempt !== null ? ` · Versuch ${attempt}${maxAttempts ? ` von ${maxAttempts}` : ''}` : '';
  switch (state.phase) {
    case 'dialing':
      return state.ringing
        ? `Klingelt${counter}. Der Zähler läuft mit, sobald die Verbindung steht.`
        : `Wählt …${counter}. Der Zähler läuft mit, sobald die Verbindung steht.`;
    case 'connected':
      return `Verbunden · ${formatCallTimer((seconds ?? 0) * 1000)}${counter}.`;
    case 'ended':
      return seconds === null
        ? `Nicht erreicht${counter}.`
        : `Gespräch beendet · ${formatCallDuration(state.view?.durationSeconds ?? seconds)}${counter}.`;
    case 'failed':
      return `Verbindung fehlgeschlagen${counter}.`;
    case 'idle':
      return '';
  }
}

function toastFor(response: LogCallResponse): string {
  if (response.dnc) return 'Anruf erfasst. Der Kontakt ist gesperrt (DNC).';
  if (response.suggestion === 'no_interest') {
    return 'Anruf erfasst. Die Versuche sind ausgeschöpft — Vorschlag: Kein Interesse.';
  }
  return 'Anruf erfasst.';
}

/**
 * Kontaktakte II.E1 „Anruf" (with III.T3/T4): disposition chips, note, follow-up → `POST /contacts/:id/calls`.
 * With telephony the softphone bar of P-45 sits above the fields; the result of a softphone call carries its
 * `callId`, without telephony the call is logged by hand (D-47).
 */
export function CallEditor({ record, task, onClose, onDone }: EditorProps) {
  const name = record.header.name;
  const telephony = useTelephonyStatus();
  const call = useCall();
  const softphone = telephony.data?.configured === true;
  const mine = call.state.contactId === record.id && call.state.source === 'record';
  const callPhase = mine ? call.state.phase : 'idle';
  const live = isLive(callPhase);
  const now = useClock(live);
  const seconds = mine ? elapsedSeconds(call.state, now) : null;
  const callId = mine && (callPhase === 'ended' || callPhase === 'failed') ? call.state.callId : null;
  const [disposition, setDisposition] = useState<CallDisposition>('reached');
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // The result follows the call: reached after a connection, „Nicht erreicht" without one.
  const preselected = useRef<string | null>(null);
  useEffect(() => {
    if (callPhase !== 'ended' || callId === null || preselected.current === callId) return;
    preselected.current = callId;
    setDisposition(seconds === null ? 'not_reached' : 'reached');
  }, [callPhase, callId, seconds]);

  // KI-Zusammenfassung of a connected softphone call: prefills note, disposition and follow-up as a suggestion while
  // the user has not chosen another disposition; never submitted by itself.
  const summaryCallId = callPhase === 'ended' && callId !== null && seconds !== null ? callId : null;
  const aiSummary = useCallSummary(summaryCallId);
  const summaryApplied = useRef<string | null>(null);
  useEffect(() => {
    if (aiSummary.status !== 'ready' || summaryCallId === null || summaryApplied.current === summaryCallId)
      return;
    summaryApplied.current = summaryCallId;
    const result = aiSummary.summary;
    setNote((value) => noteWithSummary(value, result.summary));
    const chip = editorChipOf(result.suggestedDisposition);
    if (chip !== null && disposition === 'reached') {
      setDisposition(chip);
      if (chip === 'callback') {
        setFollowUp(true);
        if (result.suggestedFollowUpAt !== null) {
          setDate(toBerlinDateKey(result.suggestedFollowUpAt));
          setTime(formatTime(result.suggestedFollowUpAt));
        } else if (!date) setDate(addDays(todayKey(), 1));
      }
    }
  }, [aiSummary, summaryCallId, disposition, date]);

  const attempts = record.properties
    .flatMap((group) => group.rows)
    .find((row) => row.key === 'attempts')?.value;
  const phone = record.header.phone ? formatPhoneDisplay(record.header.phone) : null;
  const status =
    softphone && callPhase !== 'idle'
      ? softphoneStatus(call.state, seconds, maxAttemptsOf(attempts))
      : softphone
        ? `Noch kein Anruf gestartet.${attempts ? ` Versuche bisher: ${attempts}.` : ''}`
        : `Ohne Softphone — das Ergebnis wird von Hand protokolliert.${attempts ? ` Versuche bisher: ${attempts}.` : ''}`;
  const subtitle =
    softphone && call.state.deviceReady
      ? `${phone ?? 'keine Telefonnummer'} · Softphone verbunden`
      : `${phone ?? 'keine Telefonnummer'} · ${task ? `Aufgabe: ${task.title}` : 'manuell erfasst'}`;
  const disabledReason = !softphone
    ? (telephony.data?.message ?? 'Telefonie ist nicht eingerichtet.')
    : record.header.phone === null
      ? 'Keine Telefonnummer hinterlegt.'
      : record.header.dnc
        ? 'Der Kontakt ist gesperrt (DNC).'
        : record.tools.call.enabled
          ? null
          : (record.tools.call.reason ?? 'Anrufe sind für diesen Kontakt gesperrt.');
  const taskTitle = followUpTitle(disposition, name);

  function buildBody(): LogCallBody | null {
    const next: Errors = {};
    const text = note.trim();
    if ((disposition === 'no_interest' || disposition === 'dnc') && text === '') {
      next.note = 'Bitte geben Sie einen Grund an.';
    }
    if (disposition === 'callback' && !(followUp && date)) {
      next.followUp = 'Bitte geben Sie eine Wiedervorlage an.';
    }
    if (followUp && !validTime(time)) next.followUp = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
    setErrors(next);
    if (Object.keys(next).length > 0) return null;
    // `callId` binds the result to the softphone call (api-dialer.md „Contact record E1").
    const body: LogCallBody & { callId?: string } = { disposition };
    if (callId !== null) body.callId = callId;
    if (text) body.note = text;
    const due = disposition === 'dnc' ? undefined : followUpOf(followUp, date, time);
    if (due) body.followUp = due;
    return body;
  }

  const gate = useNextStepGate(name, async (step) => {
    const body = buildBody();
    if (!body || !task) return;
    if (step.kind === 'follow_up') {
      // The call route creates the follow-up itself and completes the task after it.
      const response = await logCall(record.id, {
        ...body,
        taskId: task.id,
        followUp: step.dueTime ? { dueOn: step.dueOn, dueTime: step.dueTime } : { dueOn: step.dueOn },
      });
      if (callId !== null) call.reset();
      onDone({ toast: toastFor(response) });
      return;
    }
    // Any other step: log the call (the task stays open meanwhile), then complete the task with the step.
    const response = await logCall(record.id, body);
    if (callId !== null) call.reset();
    const done = await completeTask(task.id, { outcomeType: 'called', nextStep: step });
    onDone({
      toast: toastFor(response),
      next:
        done.next === 'book_appointment'
          ? { editor: 'appointment', completesTaskId: done.createdTaskId }
          : undefined,
    });
  });

  async function submit() {
    const body = buildBody();
    if (!body) return;
    setPending(true);
    setBanner(null);
    try {
      const response = await logCall(record.id, task ? { ...body, taskId: task.id } : body);
      if (callId !== null) call.reset();
      onDone({ toast: toastFor(response) });
    } catch (error) {
      if (isNextStepRequired(error) && task) gate.open();
      else if (isValidation(error)) {
        const fields = error.fields;
        setErrors({
          note: fields['note'],
          followUp: fields['followUp'] ?? fields['followUp.dueOn'] ?? fields['followUp.dueTime'],
        });
        if (!fields['note'] && !fields['followUp'] && !fields['followUp.dueOn']) setBanner(error.message);
      } else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <EditorCard
        tone="ink"
        dot="var(--accent)"
        title={`Anruf · ${name}`}
        subtitle={subtitle}
        footnote="Jede nicht-finale Disposition erzeugt automatisch die Folge-Task."
        label="Anruf-Editor"
        onClose={onClose}
        primary={
          <PrimaryAction loading={pending} disabled={live} onClick={() => void submit()}>
            Ergebnis speichern
          </PrimaryAction>
        }
      >
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {telephony.data ? (
          <CallBar
            contactId={record.id}
            phoneDisplay={phone}
            taskId={task?.id ?? null}
            disabledReason={disabledReason}
          />
        ) : null}
        {live && telephony.data?.transcription ? (
          <LiveTranscriptPanel callId={call.state.callId} contactName={name} compact />
        ) : null}
        <EditorField label="Status">
          <p className={styles.text}>{status}</p>
          {aiSummary.status === 'loading' || aiSummary.status === 'ready' ? (
            <p className={styles.text} aria-live="polite">
              {aiSummary.status === 'loading' ? SUMMARY_LOADING_TEXT : SUMMARY_SUGGESTION_TEXT}
            </p>
          ) : null}
        </EditorField>
        <EditorField label="Disposition" hint="Pflicht">
          <ChipGroup
            label="Disposition"
            options={DISPOSITIONS}
            value={disposition}
            onChange={(value) => {
              setDisposition(value);
              setErrors({});
              if (value === 'callback' && !followUp) {
                setFollowUp(true);
                if (!date) setDate(addDays(todayKey(), 1));
              }
            }}
          />
        </EditorField>
        <EditorField
          label={
            disposition === 'dnc' || disposition === 'no_interest'
              ? 'Gesprächsnotiz · Grund'
              : 'Gesprächsnotiz'
          }
          hint="geht in die Timeline"
          error={errors.note}
        >
          <textarea
            className={styles.textarea}
            rows={2}
            aria-label="Gesprächsnotiz"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </EditorField>
        {taskTitle ? (
          <FollowUpField
            checked={followUp}
            onCheckedChange={setFollowUp}
            date={date}
            time={time}
            onDateChange={setDate}
            onTimeChange={setTime}
            taskTitle={taskTitle}
            error={errors.followUp}
          />
        ) : null}
      </EditorCard>
      {gate.dialog}
    </>
  );
}
